<template>
  <ModalComp
    :is-visible="isVisible"
    title="Téléchargement des étiquettes de groupage"
    size="xl"
    :hide-footer="true"
    @update:is-visible="$emit('update:isVisible', $event)"
  >
    <template #body>
      <div class="space-y-6">
        <div
          v-if="requireInvoice && !hasInvoice"
          class="rounded-lg border border-yellow-200 bg-yellow-50 p-4"
        >
          <h5 class="mb-3 text-sm font-semibold text-red-600">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            Facture requise
          </h5>
          <UploadZone
            :order-id="orderId"
            :order-group-id="orderGroupId || 0"
            :order-date="orderDate || new Date().toISOString()"
            :invoice-id="invoiceId"
            @invoice-uploaded="() => emit('invoice-uploaded')"
          />
        </div>

        <div class="rounded-lg border border-gray-200 bg-white p-3 sm:p-6">
          <h4 class="mb-4 font-bold text-black">Préparation de l’expédition</h4>
          <div class="rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4">
            <h5 class="mb-4 font-semibold text-black">
              <i class="fas fa-ruler-combined mr-2 text-blue-600"></i>
              Dimensions des colis
            </h5>
            <p class="mb-4 text-sm text-black">
              Veuillez renseigner les dimensions en centimètres (cm) et le poids pour chaque colis.
              Tous les champs sont obligatoires. Si un colis dépasse le gabarit indiqué,
              répartissez son contenu dans deux colis ou plus.
            </p>
            <p class="mb-2 text-xs font-medium text-black">{{ dimensionLimitsHint }}</p>
            <div class="space-y-4">
              <div
                v-for="(colis, index) in editableDimensions"
                :key="index"
                class="rounded-lg border border-gray-300 bg-white p-3 sm:p-4"
              >
                <div class="mb-3 flex items-center justify-between">
                  <span class="font-medium text-black">Colis n°{{ index + 1 }}</span>
                  <button
                    v-if="editableDimensions.length > 1"
                    type="button"
                    class="text-sm text-red-600 hover:text-red-700"
                    :disabled="loading || isRemoving"
                    @click="removeParcel(index)"
                  >
                    <i class="fas fa-trash-alt mr-1"></i>Supprimer
                  </button>
                </div>

                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <label class="text-sm text-black">
                    Longueur (cm) <span class="text-red-600">*</span>
                    <input
                      v-model.number="colis.longueur"
                      type="number"
                      min="0"
                      step="0.1"
                      :disabled="loading || isRemoving"
                      class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                    />
                  </label>
                  <label class="text-sm text-black">
                    Largeur (cm) <span class="text-red-600">*</span>
                    <input
                      v-model.number="colis.largeur"
                      type="number"
                      min="0"
                      step="0.1"
                      :disabled="loading || isRemoving"
                      class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                    />
                  </label>
                  <label class="text-sm text-black">
                    Hauteur (cm) <span class="text-red-600">*</span>
                    <input
                      v-model.number="colis.hauteur"
                      type="number"
                      min="0"
                      step="0.1"
                      :disabled="loading || isRemoving"
                      class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                    />
                  </label>
                  <label class="text-sm text-black">
                    Poids (kg) <span class="text-red-600">*</span>
                    <input
                      v-model.number="colis.poids"
                      type="number"
                      min="0"
                      step="0.01"
                      :disabled="loading || isRemoving"
                      class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                    />
                  </label>
                </div>
                <p v-if="parcelError(colis)" class="mt-3 text-sm font-medium text-red-600">
                  <i class="fas fa-exclamation-circle mr-1"></i>
                  {{ parcelError(colis) }}
                </p>
              </div>
            </div>

            <ParcelWeightWarning
              show-comparison
              :calculated-weight="calculatedWeight"
              :parcels="editableDimensions"
            />

            <button
              type="button"
              class="mt-4 px-4 py-2 border border-blue-500 rounded-md text-sm font-medium text-blue-600 hover:border-blue-700 hover:text-blue-700 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed"
              :disabled="loading || isRemoving"
              @click="addParcel"
            >
              <i class="fas fa-plus mr-2"></i>Ajouter un colis
            </button>

          </div>
        </div>

        <div class="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p class="text-sm text-amber-900">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            <strong>Attention :</strong> En cas de contrôle transporteur, si les dimensions ou le poids renseignés ne correspondent pas aux valeurs réelles et qu’une surfacturation nous est appliquée, celle-ci sera à votre charge.
          </p>
        </div>

        <div class="flex flex-col items-stretch gap-2">
          <button
            type="button"
            class="w-full rounded-lg px-6 py-3 text-base font-semibold transition-colors disabled:cursor-not-allowed"
            :class="canConfirm ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-400 text-black'"
            :disabled="!canConfirm || loading || isRemoving"
            @click="handleConfirm"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin mr-1"></i>
            {{ loading ? "Enregistrement..." : "Confirmer et télécharger" }}
          </button>
          <p v-if="requireInvoice && !hasInvoice" class="text-center text-xs text-red-600">
            <i class="fas fa-exclamation-triangle mr-1"></i>Veuillez d'abord uploader une facture
          </p>
        </div>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import ModalComp from "@/components/ui/ModalComp.vue";
import ParcelWeightWarning from "@/components/order/ParcelWeightWarning.vue";
import UploadZone from "@/components/order/UploadZone.vue";
import { removeParcelDimension } from "@/api/orders";
import type { ParcelDimension } from "@/types";
import {
  getCarrierDimensionLimits,
  getDimensionLimitsHint,
  validateColisDimensions,
} from "@/utils/parcelDimensionsLimits";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    orderId: number;
    initialDimensions: ParcelDimension[];
    calculatedWeight?: number | null;
    idLivraison?: number | null;
    countryCode?: string | null;
    postalCode?: string | null;
    loading?: boolean;
    requireInvoice?: boolean;
    hasInvoice?: boolean;
    orderGroupId?: number;
    orderDate?: string;
    invoiceId?: string;
  }>(),
  {
    requireInvoice: false,
    hasInvoice: false,
  },
);

const emit = defineEmits<{
  "update:isVisible": [value: boolean];
  confirm: [dimensions: ParcelDimension[]];
  removed: [];
  "remove-error": [message: string];
  "invoice-uploaded": [];
}>();

const editableDimensions = ref<ParcelDimension[]>([]);
const isRemoving = ref(false);

const cloneDimensions = (list: ParcelDimension[]): ParcelDimension[] =>
  list.map((colis) => ({
    longueur: colis.longueur,
    largeur: colis.largeur,
    hauteur: colis.hauteur,
    poids: colis.poids,
  }));

watch(
  () => props.isVisible,
  (visible) => {
    if (!visible) return;
    editableDimensions.value =
      props.initialDimensions.length > 0
        ? cloneDimensions(props.initialDimensions)
        : [{ longueur: null, largeur: null, hauteur: null, poids: null }];
  },
);

const dimensionLimitsHint = computed(() =>
  getDimensionLimitsHint(props.idLivraison ?? null, props.countryCode, props.postalCode),
);

const dimensionLimits = computed(() =>
  getCarrierDimensionLimits(props.idLivraison ?? null, props.countryCode, props.postalCode),
);

const parcelError = (colis: ParcelDimension): string => {
  const { longueur, largeur, hauteur, poids } = colis;
  if (longueur == null || largeur == null || hauteur == null || poids == null)
    return "Toutes les dimensions et le poids sont obligatoires.";
  if (longueur <= 0 || largeur <= 0 || hauteur <= 0 || poids <= 0)
    return "Toutes les dimensions et le poids doivent être supérieurs à zéro.";
  const dimensionsError = validateColisDimensions(
    longueur,
    largeur,
    hauteur,
    dimensionLimits.value,
  );
  if (dimensionsError) return `Gabarit dépassé : ${dimensionsError}.`;
  if (poids > dimensionLimits.value.poids)
    return `Poids ${poids} kg (maximum ${dimensionLimits.value.poids} kg).`;
  return "";
};

const canConfirm = computed(
  () =>
    editableDimensions.value.length > 0 &&
    editableDimensions.value.every((colis) => !parcelError(colis)) &&
    (!props.requireInvoice || props.hasInvoice),
);

const addParcel = () => {
  editableDimensions.value.push({
    longueur: null,
    largeur: null,
    hauteur: null,
    poids: null,
  });
};

const removeParcel = async (index: number) => {
  if (editableDimensions.value.length <= 1 || isRemoving.value) return;
  isRemoving.value = true;
  try {
    await removeParcelDimension(props.orderId, index);
    editableDimensions.value.splice(index, 1);
    emit("removed");
  } catch (error) {
    emit(
      "remove-error",
      error instanceof Error ? error.message : "Erreur lors de la suppression",
    );
  } finally {
    isRemoving.value = false;
  }
};

const handleConfirm = () => {
  if (!canConfirm.value || props.loading) return;
  emit("confirm", cloneDimensions(editableDimensions.value));
};
</script>
